/**
 * DesignPackage.js — the canonical, storage-agnostic representation of a
 * design (see docs plan "Design Packages"). Every persistence backend
 * (ZIP, Git, browser IndexedDB, Tauri/SQLite) reads from or writes into this
 * one shape via toFileList()/fromFileList() — none of them invent their own
 * package layout.
 *
 * A package is: a manifest (small, machine-generated discovery metadata) plus
 * every file that belongs to the package directory — manifest.json,
 * design.json, preview.png, resource folders (assets/, fonts/, ...), and any
 * file/directory the Designer doesn't currently recognize. Unknown content is
 * never dropped: it round-trips through files unchanged, which is what lets
 * future package extensions (scripts/, translations/, ...) exist without
 * touching this model.
 */

import { base64ToText } from './base64.js'
import { serializeStorageValue } from '../utils/storageSerialization.js'

const MANIFEST_PATH = 'manifest.json'

// Known manifest fields this module actively reads/writes. Every other
// top-level key in manifest.json is preserved verbatim (see fromFileList/
// toFileList below) — manifest.json is generated, but never lossy.
const KNOWN_MANIFEST_FIELDS = ['id', 'name', 'description', 'author', 'entry', 'preview']

export class DesignPackage {
  constructor(manifest = {}, files = new Map()) {
    this.manifest = manifest
    this.files = files // relPath -> { content, encoding: 'utf8'|'base64', mime }
  }

  getFile(relPath) {
    return this.files.get(relPath) ?? null
  }

  setFile(relPath, entry) {
    this.files.set(relPath, entry)
  }

  deleteFile(relPath) {
    this.files.delete(relPath)
  }

  /** All files whose path starts with `${folder}/`, keyed by the remainder of the path. */
  filesUnder(folder) {
    const prefix = folder.replace(/\/$/, '') + '/'
    const out = new Map()
    for (const [path, entry] of this.files) {
      if (path.startsWith(prefix)) out.set(path.slice(prefix.length), entry)
    }
    return out
  }

  /** Read design.json (parsed JSON, not JSONL — see plan's "Design.json format"). */
  getDesignJson() {
    const entry = this.getFile(this.manifest.entry || 'design.json')
    if (!entry) return null
    const text = entry.encoding === 'base64' ? base64ToText(entry.content) : entry.content
    return JSON.parse(text)
  }

  setDesignJson(obj) {
    this.setFile(this.manifest.entry || 'design.json', {
      content: JSON.stringify(serializeStorageValue(obj, '$.design')),
      encoding: 'utf8',
      mime: 'application/json',
    })
  }

  /** Flat [{path, content, encoding, mime}] — the one interchange format every backend serializes. */
  toFileList() {
    const list = [{
      path: MANIFEST_PATH,
      content: JSON.stringify(serializeStorageValue(this.manifest, '$.manifest'), null, 2),
      encoding: 'utf8',
      mime: 'application/json',
    }]
    for (const [path, entry] of this.files) {
      list.push({ path, content: entry.content, encoding: entry.encoding, mime: entry.mime })
    }
    return list
  }

  static fromFileList(entries) {
    const files = new Map()
    let manifest = {}
    for (const { path, content, encoding, mime } of entries) {
      if (path === MANIFEST_PATH) {
        manifest = parseManifest(content, encoding)
        continue
      }
      files.set(path, { content, encoding, mime })
    }
    return new DesignPackage(manifest, files)
  }

  /**
   * Rebuild the manifest from live session fields (name/description/author),
   * preserving any unknown keys already present. manifest.json is always
   * regenerated on save — the user never hand-edits it, and it is never a
   * second source of truth for the design's own metadata.
   *
   * `id` is immutable once a package has one: it's the package's stable
   * identity (see plan "Package identity"), independent of its display name
   * or storage directory, so an already-set manifest.id always wins over
   * whatever `id` is passed in here — the parameter only seeds a brand-new
   * package that doesn't have one yet.
   */
  regenerateManifest({ id, name, description, author, entry = 'design.json', preview = 'preview.png' }) {
    const unknown = {}
    for (const [key, value] of Object.entries(this.manifest)) {
      if (!KNOWN_MANIFEST_FIELDS.includes(key)) unknown[key] = value
    }
    const stableId = this.manifest.id ?? id
    this.manifest = { id: stableId, name, description, author, entry, preview, ...unknown }
  }
}

function parseManifest(content, encoding) {
  try {
    const text = encoding === 'base64' ? base64ToText(content) : content
    const parsed = JSON.parse(text)
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}
